/**
 * Sidebar expands in place; one panel at a time (search, cart, account, market)
 */
function lophielChromeInit() {
  const sidebar = document.querySelector('[data-sidebar]');
  if (!sidebar) {
    window.LophielChrome?.destroy?.();
    document.body.classList.remove('lophiel-chrome-open');
    document.querySelectorAll('#main-content, .lophiel-footer').forEach((element) => {
      element.inert = false;
    });
    return;
  }
  if (sidebar.dataset.chromeBound === 'true') return;

  window.LophielChrome?.destroy?.();
  sidebar.dataset.chromeBound = 'true';

  const eventController = new AbortController();
  const { signal } = eventController;
  const resizeObservers = [];

  const panelsWrap = sidebar.querySelector('[data-sidebar-panels]');
  const panels = sidebar.querySelectorAll('[data-chrome-panel]');
  const desktopNavToggles = sidebar.querySelectorAll('[data-nav-toggle]');
  const desktopSubmenus = sidebar.querySelectorAll('[data-nav-submenu]');
  const searchInput = sidebar.querySelector('[data-chrome-search-input]');
  const searchForm = sidebar.querySelector('[data-chrome-search-form]');
  const searchResults = sidebar.querySelector('[data-chrome-search-results]');
  const headerSearchWrap = sidebar.querySelector('.lophiel-header-actions-wrap');
  const headerSearchInput = sidebar.querySelector('[data-header-search-input]');
  const headerSearchForm = sidebar.querySelector('[data-header-search-form]');
  const headerSearchToggle = sidebar.querySelector('[data-header-search-toggle]');
  const headerSearchResults = sidebar.querySelector('[data-header-search-results]');
  const localizationForm = document.getElementById('LophielLocalizationForm');
  const countryInput = sidebar.querySelector('[data-localization-country-input]');
  const marketSearch = sidebar.querySelector('[data-market-search]');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const searchCopy = {
    loading: searchResults?.dataset.searchLoading || 'Searching…',
    empty: searchResults?.dataset.searchEmpty || 'No products found',
    viewAll: searchResults?.dataset.searchViewAll || 'View all results',
  };
  const headerSearchCopy = {
    loading: headerSearchResults?.dataset.searchLoading || 'Searching…',
    empty: headerSearchResults?.dataset.searchEmpty || 'No products found',
    viewAll: headerSearchResults?.dataset.searchViewAll || 'View all results',
  };

  let activePanel = null;
  let activeDesktopSubmenu = null;
  let searchTimer = null;
  let searchRequestId = 0;
  let headerSearchTimer = null;
  let headerSearchRequestId = 0;
  let returnFocus = null;
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function setPageInert(inert) {
    document.querySelectorAll('#main-content, .lophiel-footer').forEach((element) => {
      element.inert = inert;
    });
  }

  function setBodyLock(lock) {
    document.body.classList.toggle('lophiel-chrome-open', lock);
    setPageInert(lock);
  }

  function releaseStaleBodyLock() {
    const hasVisiblePanel = sidebar.querySelector('[data-chrome-panel]:not([hidden])');
    if (!sidebar.isConnected || !sidebar.classList.contains('is-expanded') || !hasVisiblePanel) {
      setBodyLock(false);
    }
  }

  function setActiveTriggers(name) {
    document.querySelectorAll('[data-chrome-open]').forEach((el) => {
      el.classList.toggle('is-active', !!name && el.dataset.chromeOpen === name);
      el.setAttribute('aria-expanded', el.dataset.chromeOpen === name ? 'true' : 'false');
    });
  }

  function showPanel(name) {
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.chromePanel !== name;
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
    });
    activePanel = name;
    setActiveTriggers(name);
  }

  function clearSearchResults() {
    if (!searchResults) return;
    searchResults.innerHTML = '';
    searchResults.setAttribute('hidden', '');
    searchResults.classList.remove('is-loading', 'is-empty');
  }

  function clearMarketSearch() {
    if (!marketSearch) return;
    marketSearch.value = '';
    sidebar.querySelectorAll('[data-market-item]').forEach((item) => {
      item.hidden = false;
    });
  }

  function emitChromeChange() {
    document.dispatchEvent(new CustomEvent('lophiel-chrome-change'));
  }

  function closeDesktopSubmenu() {
    if (!activeDesktopSubmenu) return;
    activeDesktopSubmenu.hidden = true;
    activeDesktopSubmenu = null;
    sidebar.classList.remove('is-nav-expanded');
    sidebar.style.removeProperty('--sidebar-submenu-open-width');
    desktopNavToggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  }

  function focusPanel(name) {
    const panel = sidebar.querySelector(`[data-chrome-panel="${name}"]:not([hidden])`);
    if (!panel) return;
    const preferred = name === 'search' ? searchInput : name === 'localization' ? marketSearch : null;
    const target = preferred || panel.querySelector(focusableSelector);
    target?.focus({ preventScroll: true });
  }

  function trapPanelFocus(event) {
    const panel = sidebar.querySelector('[data-chrome-panel]:not([hidden])');
    if (!panel) return;
    const focusable = [...panel.querySelectorAll(focusableSelector)].filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openDesktopSubmenu(id) {
    if (isMobileChrome()) return;
    const target = sidebar.querySelector(`[data-nav-submenu="${id}"]`);
    if (!target) return;

    if (activeDesktopSubmenu && activeDesktopSubmenu === target) {
      closeDesktopSubmenu();
      return;
    }

    desktopSubmenus.forEach((menu) => {
      menu.hidden = true;
    });
    desktopNavToggles.forEach((btn) => {
      btn.setAttribute('aria-expanded', btn.dataset.navToggle === id ? 'true' : 'false');
    });
    target.hidden = false;
    activeDesktopSubmenu = target;
    sidebar.classList.add('is-nav-expanded');
    target.style.removeProperty('top');
    const submenuWidth = Math.ceil(target.getBoundingClientRect().width);
    sidebar.style.setProperty('--sidebar-submenu-open-width', `${submenuWidth}px`);
  }

  function isMobileChrome() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function syncMobileChromeLayout() {
    const docEl = document.documentElement;

    if (!isMobileChrome()) {
      docEl.classList.remove('lophiel-mobile-layout');
      docEl.style.removeProperty('--lophiel-announcement-height');
      docEl.style.removeProperty('--lophiel-mobile-bar-height');
      docEl.style.removeProperty('--lophiel-mobile-chrome-top');
      docEl.style.removeProperty('--lophiel-mobile-top-chrome');
      docEl.style.removeProperty('--lophiel-mobile-chrome-height');
      return;
    }

    docEl.classList.add('lophiel-mobile-layout');

    const announcement = document.querySelector('.shopify-section.announcement-bar-section');
    const rail = sidebar.querySelector('[data-sidebar-rail]');
    const annHeight = announcement?.getBoundingClientRect().height ?? 0;
    const barHeight = rail?.getBoundingClientRect().height ?? 0;
    const chromeTop = annHeight + barHeight;

    docEl.style.setProperty('--lophiel-announcement-height', `${annHeight}px`);
    docEl.style.setProperty('--lophiel-mobile-bar-height', `${barHeight}px`);
    docEl.style.setProperty('--lophiel-mobile-chrome-top', `${chromeTop}px`);
    docEl.style.setProperty('--lophiel-mobile-top-chrome', `${chromeTop}px`);
    docEl.style.setProperty('--lophiel-mobile-chrome-height', `${barHeight}px`);
  }

  function open(name) {
    if (!name) return;
    clearHeaderSearchResults();
    setHeaderSearchOpen(false);
    closeDesktopSubmenu();

    if (sidebar.classList.contains('is-expanded') && activePanel === name) {
      close();
      return;
    }

    if (!sidebar.classList.contains('is-expanded')) returnFocus = document.activeElement;
    syncMobileChromeLayout();
    showPanel(name);
    panelsWrap?.removeAttribute('hidden');
    requestAnimationFrame(() => {
      syncMobileChromeLayout();
      sidebar.classList.add('is-expanded');
      focusPanel(name);
    });
    setBodyLock(true);
    emitChromeChange();

    if (name === 'cart' && window.LophielCart?.refresh) {
      window.LophielCart.refresh();
    }
  }

  function close() {
    sidebar.classList.remove('is-expanded');
    closeDesktopSubmenu();
    setBodyLock(false);
    setActiveTriggers(null);
    clearSearchResults();
    clearMarketSearch();
    panelsWrap?.setAttribute('hidden', '');
    panels.forEach((panel) => (panel.hidden = true));
    activePanel = null;
    emitChromeChange();
    const focusTarget = returnFocus;
    returnFocus = null;
    requestAnimationFrame(() => {
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
    });
  }

  document.addEventListener('click', (e) => {
    const navToggle = e.target.closest('[data-nav-toggle]');
    if (navToggle) {
      e.preventDefault();
      openDesktopSubmenu(navToggle.dataset.navToggle);
      return;
    }

    const opener = e.target.closest('[data-chrome-open]');
    if (opener) {
      const panel = opener.dataset.chromeOpen;
      if (!panel) return;
      e.preventDefault();
      open(panel);
      return;
    }

    if (e.target.closest('[data-chrome-close]')) {
      e.preventDefault();
      close();
      return;
    }

    if (
      sidebar.classList.contains('is-expanded') &&
      !e.target.closest('[data-sidebar]') &&
      !e.target.closest('[data-chrome-open]')
    ) {
      close();
      return;
    }

    if (
      !e.target.closest('[data-sidebar]') &&
      sidebar.classList.contains('is-nav-expanded')
    ) {
      closeDesktopSubmenu();
    }
  }, { signal });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab' && sidebar.classList.contains('is-expanded')) {
      trapPanelFocus(e);
      return;
    }
    if (e.key !== 'Escape') return;
    if (sidebar.classList.contains('is-expanded')) {
      close();
      return;
    }
    if (sidebar.classList.contains('is-nav-expanded')) closeDesktopSubmenu();
  }, { signal });

  sidebar.querySelectorAll('.lophiel-mobile-chrome__nav-link').forEach((link) => {
    link.addEventListener('click', () => close(), { signal });
  });

  sidebar.querySelectorAll('[data-country-code]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const code = btn.dataset.countryCode;
      if (!code || !localizationForm || !countryInput) return;
      countryInput.value = code;
      localizationForm.submit();
    }, { signal });
  });

  marketSearch?.addEventListener('input', () => {
    const q = marketSearch.value.trim().toLowerCase();
    sidebar.querySelectorAll('[data-market-item]').forEach((item) => {
      const btn = item.querySelector('[data-country-code]');
      const haystack = btn?.dataset.marketFilter || '';
      item.hidden = !!(q && !haystack.includes(q));
    });
  }, { signal });

  function chevronRightIconHtml() {
    const tpl = document.getElementById('lophiel-tpl-chevron-right');
    return tpl?.innerHTML?.trim() || '';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(price) {
    if (price == null || price === '') return '';
    const n = Number(price);
    if (Number.isNaN(n)) return escapeHtml(price);
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(n);
    }
    return `$${(n / 100).toFixed(2)}`;
  }

  async function fetchSearchSuggestions(q) {
    const types = ['product', 'article', 'page', 'collection'];
    const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=${types.join(',')}&resources[limit]=8&resources[options][unavailable_products]=hide`;
    const res = await fetch(url);
    if (!res.ok) return { products: [], articles: [], pages: [], collections: [] };
    const data = await res.json();
    const results = data?.resources?.results || {};
    return {
      products: results.products || [],
      articles: results.articles || [],
      pages: results.pages || [],
      collections: results.collections || [],
    };
  }

  function renderSearchItem(item, type) {
    const title = escapeHtml(item.title || '');
    const url = escapeHtml(item.url || '#');
    const image =
      item.image || item.featured_image?.url || item.featured_image || '';
    const price =
      type === 'product'
        ? formatMoney(item.price ?? item.price_min ?? item.price_max)
        : '';

    return `<li class="lophiel-chrome__search-item">
      <a href="${url}" class="lophiel-chrome__search-link">
        ${
          image
            ? `<img class="lophiel-chrome__search-thumb" src="${escapeHtml(image)}" alt="" width="48" height="48" loading="lazy">`
            : `<span class="lophiel-chrome__search-thumb lophiel-chrome__search-thumb--empty" aria-hidden="true"></span>`
        }
        <span class="lophiel-chrome__search-meta">
          <span class="lophiel-chrome__search-title">${title}</span>
          ${price ? `<span class="lophiel-chrome__search-price">${price}</span>` : ''}
        </span>
      </a>
    </li>`;
  }

  function renderSearchResults(payload, query) {
    if (!searchResults) return;

    const items = [
      ...payload.products.map((p) => ({ ...p, _type: 'product' })),
      ...payload.collections.map((p) => ({ ...p, _type: 'collection' })),
      ...payload.articles.map((p) => ({ ...p, _type: 'article' })),
      ...payload.pages.map((p) => ({ ...p, _type: 'page' })),
    ].slice(0, 8);

    searchResults.classList.remove('is-loading');

    if (!items.length) {
      searchResults.classList.add('is-empty');
      searchResults.innerHTML = `<p class="lophiel-chrome__search-message">${escapeHtml(searchCopy.empty)}</p>`;
      searchResults.removeAttribute('hidden');
      return;
    }

    searchResults.classList.remove('is-empty');
    const list = items.map((item) => renderSearchItem(item, item._type)).join('');
    const viewAllUrl = `${root}search?q=${encodeURIComponent(query)}`;
    searchResults.innerHTML = `<ul class="lophiel-chrome__search-list">${list}</ul>
      <a href="${escapeHtml(viewAllUrl)}" class="lophiel-chrome__search-view-all lophiel-link-with-chevron">
        <span>${escapeHtml(searchCopy.viewAll)}</span>${chevronRightIconHtml()}
      </a>`;
    searchResults.removeAttribute('hidden');
  }

  function setHeaderSearchExpanded(expanded) {
    headerSearchInput?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function setHeaderSearchOpen(open, { focus = false } = {}) {
    if (!headerSearchForm || !headerSearchInput || !headerSearchToggle) return;
    headerSearchForm.dataset.searchOpen = open ? 'true' : 'false';
    headerSearchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    headerSearchInput.tabIndex = open ? 0 : -1;

    if (focus && open) {
      requestAnimationFrame(() => headerSearchInput.focus({ preventScroll: true }));
    }
  }

  function clearHeaderSearchResults() {
    if (!headerSearchResults) return;
    headerSearchResults.innerHTML = '';
    headerSearchResults.setAttribute('hidden', '');
    headerSearchResults.classList.remove('is-loading', 'is-empty');
    setHeaderSearchExpanded(false);
  }

  function showHeaderSearchLoading() {
    if (!headerSearchResults) return;
    headerSearchResults.classList.add('is-loading');
    headerSearchResults.classList.remove('is-empty');
    headerSearchResults.innerHTML = `<p class="lophiel-chrome__search-message">${escapeHtml(headerSearchCopy.loading)}</p>`;
    headerSearchResults.removeAttribute('hidden');
    setHeaderSearchExpanded(true);
  }

  function renderHeaderSearchResults(payload, query) {
    if (!headerSearchResults) return;
    const products = payload.products.slice(0, 5);
    headerSearchResults.classList.remove('is-loading');

    if (!products.length) {
      headerSearchResults.classList.add('is-empty');
      headerSearchResults.innerHTML = `<p class="lophiel-chrome__search-message">${escapeHtml(headerSearchCopy.empty)}</p>`;
      headerSearchResults.removeAttribute('hidden');
      setHeaderSearchExpanded(true);
      return;
    }

    headerSearchResults.classList.remove('is-empty');
    const list = products.map((product) => renderSearchItem(product, 'product')).join('');
    const viewAllUrl = `${root}search?q=${encodeURIComponent(query)}&type=product`;
    headerSearchResults.innerHTML = `<ul class="lophiel-chrome__search-list">${list}</ul>
      <a href="${escapeHtml(viewAllUrl)}" class="lophiel-chrome__search-view-all lophiel-link-with-chevron">
        <span>${escapeHtml(headerSearchCopy.viewAll)}</span>${chevronRightIconHtml()}
      </a>`;
    headerSearchResults.removeAttribute('hidden');
    setHeaderSearchExpanded(true);
  }

  async function runHeaderSearch(q) {
    const requestId = ++headerSearchRequestId;
    showHeaderSearchLoading();
    try {
      const payload = await fetchSearchSuggestions(q);
      if (requestId !== headerSearchRequestId) return;
      renderHeaderSearchResults(payload, q);
    } catch (_) {
      if (requestId !== headerSearchRequestId || !headerSearchResults) return;
      headerSearchResults.classList.remove('is-loading');
      headerSearchResults.classList.add('is-empty');
      headerSearchResults.innerHTML = `<p class="lophiel-chrome__search-message">${escapeHtml(headerSearchCopy.empty)}</p>`;
      headerSearchResults.removeAttribute('hidden');
      setHeaderSearchExpanded(true);
    }
  }

  function showSearchLoading() {
    if (!searchResults) return;
    searchResults.classList.add('is-loading');
    searchResults.classList.remove('is-empty');
    searchResults.removeAttribute('hidden');
    searchResults.innerHTML = `<p class="lophiel-chrome__search-message">${escapeHtml(searchCopy.loading)}</p>`;
  }

  async function runSearch(q) {
    const requestId = ++searchRequestId;
    showSearchLoading();
    try {
      const payload = await fetchSearchSuggestions(q);
      if (requestId !== searchRequestId) return;
      renderSearchResults(payload, q);
    } catch (_) {
      if (requestId !== searchRequestId) return;
      searchResults?.classList.remove('is-loading');
      searchResults?.classList.add('is-empty');
      if (searchResults) {
        searchResults.innerHTML = `<p class="lophiel-chrome__search-message">${escapeHtml(searchCopy.empty)}</p>`;
        searchResults.removeAttribute('hidden');
      }
    }
  }

  searchInput?.addEventListener('input', () => {
    const q = searchInput.value.trim();
    clearTimeout(searchTimer);
    if (q.length < 2) {
      clearSearchResults();
      return;
    }
    searchTimer = setTimeout(() => runSearch(q), 280);
  }, { signal });

  searchForm?.addEventListener('submit', (e) => {
    const q = searchInput?.value.trim();
    if (!q) {
      e.preventDefault();
      return;
    }
    if (q.length >= 2 && sidebar.classList.contains('is-expanded') && activePanel === 'search') {
      e.preventDefault();
      runSearch(q);
    }
  }, { signal });

  headerSearchInput?.addEventListener('input', () => {
    const q = headerSearchInput.value.trim();
    clearTimeout(headerSearchTimer);
    if (q.length < 2) {
      headerSearchRequestId += 1;
      clearHeaderSearchResults();
      return;
    }
    headerSearchTimer = window.setTimeout(() => runHeaderSearch(q), 240);
  }, { signal });

  headerSearchInput?.addEventListener('focus', () => {
    if (headerSearchInput.value.trim().length >= 2 && headerSearchResults?.childElementCount) {
      headerSearchResults.removeAttribute('hidden');
      setHeaderSearchExpanded(true);
    }
  }, { signal });

  headerSearchForm?.addEventListener('submit', (event) => {
    if (headerSearchForm.dataset.searchOpen !== 'true') {
      event.preventDefault();
      setHeaderSearchOpen(true, { focus: true });
      return;
    }

    if (!headerSearchInput?.value.trim()) {
      event.preventDefault();
      headerSearchInput?.focus({ preventScroll: true });
    }
  }, { signal });

  document.addEventListener('click', (event) => {
    if (headerSearchWrap && !event.target.closest('.lophiel-header-actions-wrap')) {
      clearHeaderSearchResults();
      setHeaderSearchOpen(false);
    }
  }, { signal });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && headerSearchForm?.dataset.searchOpen === 'true') {
      clearHeaderSearchResults();
      setHeaderSearchOpen(false);
      headerSearchToggle?.focus({ preventScroll: true });
    }
  }, { signal });

  setHeaderSearchOpen(Boolean(headerSearchInput?.value.trim()));

  syncMobileChromeLayout();
  window.addEventListener('resize', syncMobileChromeLayout, { passive: true, signal });
  window.addEventListener('resize', () => {
    if (isMobileChrome()) closeDesktopSubmenu();
  }, { passive: true, signal });
  window.addEventListener('load', syncMobileChromeLayout, { signal });
  const announcementSection = document.querySelector('.shopify-section.announcement-bar-section');
  if (announcementSection && 'ResizeObserver' in window) {
    const observer = new ResizeObserver(syncMobileChromeLayout);
    observer.observe(announcementSection);
    resizeObservers.push(observer);
  }
  const rail = sidebar.querySelector('[data-sidebar-rail]');
  if (rail && 'ResizeObserver' in window) {
    const observer = new ResizeObserver(syncMobileChromeLayout);
    observer.observe(rail);
    resizeObservers.push(observer);
  }

  window.addEventListener('pagehide', () => close(), { signal });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) close();
    else releaseStaleBodyLock();
    syncMobileChromeLayout();
  }, { signal });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) releaseStaleBodyLock();
  }, { signal });

  function destroy() {
    eventController.abort();
    resizeObservers.forEach((observer) => observer.disconnect());
    sidebar.classList.remove('is-expanded', 'is-nav-expanded');
    sidebar.removeAttribute('data-chrome-bound');
    setBodyLock(false);
  }

  window.LophielChrome = {
    open,
    close,
    syncLayout: syncMobileChromeLayout,
    isOpen: () => sidebar.classList.contains('is-expanded'),
    destroy,
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', lophielChromeInit);
} else {
  lophielChromeInit();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.matches('[data-sidebar]') && !event.target.querySelector('[data-sidebar]')) return;
  lophielChromeInit();
});
